import React, { useId, useMemo } from 'react';

import { MultiSelect } from 'shadcn/components/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { HARDCODED_COMPANY_SOURCES, toCompanySourceOption } from 'shared/companySource';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

const ALL_COMPANY_SOURCE_OPTIONS = HARDCODED_COMPANY_SOURCES.map(toCompanySourceOption);

function toOptionalNumericString(value) {
    if (value === null || value === undefined || value === '') return undefined;
    return String(Number(value));
}

function toNumericIdArray(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.map((v) => Number(v)).filter((id) => Number.isFinite(id));
    }
    const id = Number(value);
    return Number.isFinite(id) ? [id] : [];
}

/**
 * @param {Object} props
 * @param {'multiple'|'single'} [props.mode='multiple']
 * @param {number[]|number|null} [props.value]
 * @param {number|null} [props.defaultValue] - Uncontrolled initial value (single mode)
 * @param {(value: number[]|number|null) => void} [props.onValueChange]
 * @param {number[]} [props.eligibleSourceIds] - When set, filters options to these ids
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.label='Company Source']
 * @param {boolean} [props.showLabel=true]
 * @param {string} [props.placeholder]
 * @param {number} [props.maxCount=3]
 * @returns {JSX.Element}
 */
export function CompanySourcePicker({
    defaultValue,
    disabled = false,
    eligibleSourceIds,
    label = 'Company Source',
    maxCount = 3,
    mode = 'multiple',
    onValueChange,
    placeholder,
    showLabel = true,
    value,
}) {
    const labelId = useId();

    const options = useMemo(() => {
        if (eligibleSourceIds === undefined) {
            return ALL_COMPANY_SOURCE_OPTIONS;
        }
        if (!Array.isArray(eligibleSourceIds) || eligibleSourceIds.length === 0) {
            return [];
        }
        const allowed = new Set(eligibleSourceIds.map((id) => Number(id)));
        return ALL_COMPANY_SOURCE_OPTIONS.filter((option) => allowed.has(option.value));
    }, [eligibleSourceIds]);

    const labelElement =
        showLabel && label ? (
            <span id={labelId} className={labelClassName}>
                {label}
            </span>
        ) : null;

    if (mode === 'single') {
        const isSingleControlled = value !== undefined;

        return (
            <div className="flex flex-col gap-2">
                {labelElement}
                <Select
                    {...(isSingleControlled
                        ? { value: toOptionalNumericString(value) }
                        : { defaultValue: toOptionalNumericString(defaultValue) })}
                    onValueChange={(nextValue) => {
                        onValueChange?.(nextValue ? Number(nextValue) : null);
                    }}
                    disabled={disabled}
                >
                    <SelectTrigger
                        className="w-full"
                        aria-label={label}
                        aria-labelledby={showLabel ? labelId : undefined}
                    >
                        <SelectValue placeholder={placeholder ?? 'Select company source'} />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((option) => (
                            <SelectItem key={option.value} value={String(option.value)}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    }

    const selectedValue = toNumericIdArray(value);
    const isMultipleControlled = value !== undefined;

    return (
        <div className="flex flex-col gap-2">
            {labelElement}
            <MultiSelect
                aria-labelledby={labelId}
                options={options}
                {...(isMultipleControlled ? { value: selectedValue } : { defaultValue: selectedValue })}
                onValueChange={(newValues) => {
                    onValueChange?.(newValues.map((v) => Number(v)));
                }}
                placeholder={placeholder ?? 'Select company sources'}
                maxCount={maxCount}
                modalPopover={false}
                disabled={disabled}
            />
        </div>
    );
}

CompanySourcePicker.displayName = 'CompanySourcePicker';
